import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ApiClientError } from '@terra/api-client';
import { toast } from '@terra/ui';

import { api } from '../lib/api';

import { AUTH_SCOPED_CSS } from './authStyles';

const DEFAULT_ADMIN_LOGIN_IMAGE =
  'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80';

const TERRA_LOGO_URL =
  'https://lh3.googleusercontent.com/aida-public/AB6AXuCSmwtT-Za5b9CHsGaDhtFOTNwsZDaQldEF8lQZ9Elh4pjiafSgFIIoEK2sZDeGepJNhatnt0A_-cCJRnD1Lgx8lN4TeB8y-NrWrDTqDHyQ8QQoyco8I5YgDKEo7fb0MVYXSXbMZ82Qks4VxtGbNijakpYmro5fgKJzAtGyw5uVHt_bz5FiuQ8ACF1dRmYGcyAWcFBespTd8qFoUqwf5GTMjMJfjpSQuvXkum-3rTBxqpDGQlT4XoidUfRPUDjo4_vnKJE7Dt2tzrI';

const RESEND_COOLDOWN_SECONDS = 30;

/** The rules `Password` enforces in @terra/contracts, spelled out for the person typing. */
const RULES: Array<{ label: string; test: (v: string) => boolean }> = [
  { label: 'At least 8 characters', test: (v) => v.length >= 8 },
  { label: 'A lowercase letter', test: (v) => /[a-z]/.test(v) },
  { label: 'An uppercase letter', test: (v) => /[A-Z]/.test(v) },
  { label: 'A digit', test: (v) => /\d/.test(v) },
];

/**
 * Setting a new seller password, without anyone at Terra Shoppy touching it.
 *
 * Sign-in now needs a password as well as an SMS code, so a seller who does not
 * know their password has no way in at all — and until this page existed the
 * only route was emailing support and waiting for someone to reset it by hand.
 * That is slow for the seller and worse for us: it makes "reset this account's
 * password" a routine human request, which is exactly the shape of a successful
 * social-engineering call.
 *
 * Two steps, mirroring the sign-in screen:
 *
 *   1. `request` — the seller names the email or mobile their store is
 *      registered with and where the code should go. `scope: 'VENDOR'` makes the
 *      API answer plainly when no seller account matches (see the contract for
 *      why the seller portal trades enumeration protection away and the
 *      storefront does not).
 *   2. `reset`  — the 6-digit code plus the new password.
 *
 * The code is spent on *choosing a password*; it is not a session. Finishing
 * here lands back on the sign-in screen, where both factors are asked for
 * again.
 */
export function ForgotPasswordPage() {
  const navigate = useNavigate();

  const { data: authLayoutSetting } = useQuery({
    queryKey: ['website-public-setting', 'auth_layout'],
    queryFn: () => api.cms.getPublicSetting('auth_layout').catch(() => null),
    staleTime: 60_000,
  });
  const authLayout = (authLayoutSetting?.value as any)?.layoutId || 'modern';
  const bgImage = (authLayoutSetting?.value as any)?.adminLoginImage || DEFAULT_ADMIN_LOGIN_IMAGE;

  const [step, setStep] = useState<'request' | 'reset'>('request');
  const [channel, setChannel] = useState<'EMAIL' | 'SMS'>('EMAIL');
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [deliveredTo, setDeliveredTo] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const sendCode = useMutation({
    mutationFn: () =>
      api.auth.forgotPassword({ identifier: identifier.trim(), channel, scope: 'VENDOR' }),
    onSuccess: (res) => {
      setDeliveredTo(res.deliveredTo);
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setStep('reset');
    },
    onError: (error) => {
      toast.error(error instanceof ApiClientError ? error.message : 'Could not send the code');
    },
  });

  const resetPassword = useMutation({
    mutationFn: () =>
      api.auth.resetPasswordWithOtp({
        identifier: identifier.trim(),
        code: code.trim(),
        newPassword,
      }),
    onSuccess: () => {
      toast.success('Password updated. Sign in with your new password.');
      navigate('/login', { replace: true });
    },
    onError: (error) => {
      toast.error(error instanceof ApiClientError ? error.message : 'Could not reset the password');
    },
  });

  const isEmail = channel === 'EMAIL';
  const unmetRules = RULES.filter((r) => !r.test(newPassword));
  const mismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;
  const canSubmitReset =
    code.length === 6 && unmetRules.length === 0 && newPassword === confirmPassword;

  const activeMutation = step === 'request' ? sendCode : resetPassword;
  const error =
    activeMutation.error instanceof ApiClientError
      ? activeMutation.error.message
      : activeMutation.isError
        ? 'Something went wrong. Please try again.'
        : null;

  const isModern = authLayout === 'modern';

  const cardContent = (
    <main className="ts-login-card">
      <img
        src={TERRA_LOGO_URL}
        alt="Terra Shoppy Logo"
        className="ts-login-logo"
        draggable={false}
      />

      <h1 className="ts-login-heading">RESET YOUR PASSWORD</h1>
      <p className="ts-login-subtext">
        {step === 'request'
          ? 'Step 1 of 2 — tell us where to send your code.'
          : 'Step 2 of 2 — enter the code and choose a new password.'}
      </p>

      {step === 'request' && (
        // Both are offered because a seller who has changed their number can
        // still reach their inbox, and vice versa — losing one must not lock
        // the account.
        <div className="ts-reset-channels" style={{ marginBottom: '20px' }}>
          <button
            type="button"
            className={`ts-reset-channel ${isEmail ? 'is-active' : ''}`}
            onClick={() => setChannel('EMAIL')}
          >
            ✉️ Email
          </button>
          <button
            type="button"
            className={`ts-reset-channel ${!isEmail ? 'is-active' : ''}`}
            onClick={() => setChannel('SMS')}
          >
            📱 SMS
          </button>
        </div>
      )}

      <form
        className="ts-login-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (step === 'request') sendCode.mutate();
          else resetPassword.mutate();
        }}
      >
        {step === 'request' ? (
          <>
            <div className="ts-login-field">
              <label htmlFor="reset-identifier" className="ts-login-label">
                Registered Email or Mobile
              </label>
              <input
                id="reset-identifier"
                className="ts-login-input"
                type="text"
                inputMode={isEmail ? 'email' : 'tel'}
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                placeholder={isEmail ? 'you@example.com' : '+91 98765 43210'}
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                autoFocus
                required
              />
            </div>

            {error && <p className="ts-login-error">{error}</p>}

            <button
              type="submit"
              className="ts-login-submit"
              disabled={sendCode.isPending || !identifier.trim()}
            >
              {sendCode.isPending && <span className="ts-login-spinner" />}
              SEND CODE
            </button>
          </>
        ) : (
          <>
            <p className="ts-login-subtext" style={{ fontSize: 14, lineHeight: 1.6 }}>
              {deliveredTo ? (
                <>
                  We sent a 6-digit code to{' '}
                  <strong style={{ letterSpacing: '0.3px' }}>{deliveredTo}</strong>.
                </>
              ) : (
                // `deliveredTo` comes back null when the address cannot actually
                // be mailed or texted. Saying so beats a code that never arrives.
                <>
                  We could not deliver a code to that address. Check it and try again, or contact
                  support.
                </>
              )}
            </p>

            <div className="ts-login-field">
              <label htmlFor="reset-code" className="ts-login-label">
                6-Digit Code
              </label>
              <input
                id="reset-code"
                className="ts-login-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                autoFocus
                required
              />
            </div>

            <div className="ts-login-field">
              <label htmlFor="reset-password" className="ts-login-label">
                New Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id="reset-password"
                  className="ts-login-input"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Choose a new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{ paddingRight: '64px' }}
                  required
                />
                <button
                  type="button"
                  className="ts-login-forgot"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            {/* Shown while it still matters, so the rules are not a surprise
                delivered by the server after the code has been spent. */}
            {newPassword.length > 0 && unmetRules.length > 0 && (
              <ul
                style={{
                  margin: '-8px 0 4px',
                  paddingLeft: '18px',
                  fontSize: '13px',
                  color: '#6b7280',
                  fontFamily: 'Arial, Helvetica, sans-serif',
                }}
              >
                {unmetRules.map((rule) => (
                  <li key={rule.label}>{rule.label}</li>
                ))}
              </ul>
            )}

            <div className="ts-login-field">
              <label htmlFor="reset-confirm" className="ts-login-label">
                Confirm New Password
              </label>
              <input
                id="reset-confirm"
                className="ts-login-input"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Type it again"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            {mismatch && <p className="ts-login-error">The two passwords do not match.</p>}
            {error && <p className="ts-login-error">{error}</p>}

            <button
              type="submit"
              className="ts-login-submit"
              disabled={resetPassword.isPending || !canSubmitReset}
            >
              {resetPassword.isPending && <span className="ts-login-spinner" />}
              SET NEW PASSWORD
            </button>

            <div className="ts-login-options">
              <button
                type="button"
                className="ts-login-forgot"
                onClick={() => sendCode.mutate()}
                disabled={sendCode.isPending || cooldown > 0}
              >
                {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
              </button>
              <button
                type="button"
                className="ts-login-forgot"
                onClick={() => {
                  setStep('request');
                  setCode('');
                  setNewPassword('');
                  setConfirmPassword('');
                  sendCode.reset();
                  resetPassword.reset();
                }}
              >
                Use a different address
              </button>
            </div>
          </>
        )}
      </form>

      <p
        style={{
          marginTop: '18px',
          fontSize: '15px',
          fontFamily: 'Arial, Helvetica, sans-serif',
          textAlign: 'center',
          color: '#6b7280',
        }}
      >
        Remembered it?{' '}
        <Link to="/login" className="ts-login-forgot" style={{ textDecoration: 'none' }}>
          Back to Sign In
        </Link>
      </p>
    </main>
  );

  return (
    <>
      <style>{AUTH_SCOPED_CSS}</style>

      <div
        className={`ts-login-page ${!isModern && bgImage ? 'ts-login-page--classic' : ''}`}
        style={
          !isModern && bgImage
            ? {
                backgroundImage: `url(${bgImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : {}
        }
      >
        {isModern ? (
          <div className="ts-login-modern-container">
            <div className="ts-login-side-image">
              <img src={bgImage} alt="Vendor Login Banner" />
            </div>
            {cardContent}
          </div>
        ) : (
          cardContent
        )}
      </div>
    </>
  );
}
